import { Camera, Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/lib/format';
import { CATEGORY_LABELS, ITEM_TYPE_LABELS, type ChecklistTemplate } from './types';

interface ChecklistTemplateDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: ChecklistTemplate | null;
  onEdit?: () => void;
}

export function ChecklistTemplateDetailDialog({
  open,
  onOpenChange,
  template,
  onEdit,
}: ChecklistTemplateDetailDialogProps) {
  if (!template) return null;

  // Agrupa itens por "group" preservando ordem
  const groups: Array<{ name: string; items: typeof template.items }> = [];
  for (const item of [...template.items].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const groupName = item.group ?? 'Geral';
    const existing = groups.find((g) => g.name === groupName);
    if (existing) existing.items.push(item);
    else groups.push({ name: groupName, items: [item] });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{template.name}</DialogTitle>
          <DialogDescription>
            {template.description || 'Sem descrição.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Metadados */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{CATEGORY_LABELS[template.category]}</Badge>
            {template.isActive ? (
              <Badge variant="success">Ativo</Badge>
            ) : (
              <Badge variant="outline">Inativo</Badge>
            )}
            <Badge variant="outline">v{template.version}</Badge>
            <Badge variant="outline">{template.items.length} {template.items.length === 1 ? 'item' : 'itens'}</Badge>
          </div>

          <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
            <div>
              <span className="font-medium text-foreground">Criado em:</span>{' '}
              {formatDateTime(template.createdAt)}
            </div>
            <div>
              <span className="font-medium text-foreground">Última atualização:</span>{' '}
              {formatDateTime(template.updatedAt)}
            </div>
          </div>

          {/* Itens agrupados */}
          <div className="space-y-4">
            {groups.map((group) => (
              <div key={group.name} className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.name}
                </h3>
                <div className="space-y-2">
                  {group.items.map((item, idx) => (
                    <div
                      key={item.id}
                      className="flex items-start gap-3 rounded-lg border border-border bg-card p-3"
                    >
                      <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold text-muted-foreground">
                        {idx + 1}
                      </div>
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-medium">
                          {item.label}
                          {item.required && (
                            <span className="ml-1 text-destructive" title="Obrigatório">
                              *
                            </span>
                          )}
                        </p>
                        {item.description && (
                          <p className="text-xs text-muted-foreground">
                            {item.description}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-1.5 pt-1">
                          <Badge variant="outline" className="text-[10px]">
                            {ITEM_TYPE_LABELS[item.type]}
                          </Badge>
                          {item.required && (
                            <Badge variant="outline" className="gap-1 text-[10px]">
                              <Check className="size-3" /> Obrigatório
                            </Badge>
                          )}
                          {item.photoRequired && (
                            <Badge variant="outline" className="gap-1 text-[10px]">
                              <Camera className="size-3" /> Foto
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          {onEdit && (
            <Button onClick={onEdit}>
              Editar modelo
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
